"use client";

import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import resourceTimeGridPlugin from "@fullcalendar/resource-timegrid";
import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import Link from "next/link";
import "@/styles/calendar.css";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Plus,
  RefreshCw,
  Building2,
  MapPin,
  Calendar,
  Clock,
  Users,
  User,
  CreditCard,
  FileText,
  MessageSquare,
  AlertTriangle,
  Ban,
  Route,
  Search,
  X,
  Handshake,
  Rows3,
  Gift,
} from "lucide-react";
import { LanguageFlag } from "@/components/ui/language-flag";
import { PORTAL_ROUTES } from "@/lib/routing/portal-routes";
import { useRouter } from "next/navigation";
import { toast } from "sonner";
import BookingFilters from "@/components/bookings/BookingFilters";
import SearchDropdown from "@/components/booking/search/SearchDropdown";
import {
  STATUS_IDS,
  getStatusColorConfig,
  getStatusColorClasses,
} from "@/lib/constants/statuses";
import { BOOKING_TYPE_IDS } from "@/lib/constants/booking-types";
import { formatTimeDisplay } from "@/lib/utils/time";
// Removed EventHoverCard import - using inline implementation

// Helper function to get payment status name
function getPaymentStatusName(statusId: number | null | undefined): string {
  if (!statusId) return "Unknown";

  switch (statusId) {
    case STATUS_IDS.PAYMENT_PENDING:
      return "Pending";
    case STATUS_IDS.PAYMENT_PAID:
      return "Paid";
    case STATUS_IDS.PAYMENT_FAILED:
      return "Failed";
    case STATUS_IDS.PAYMENT_REFUNDED:
      return "Refunded";
    case STATUS_IDS.PAYMENT_PARTIAL:
      return "Partial";
    case STATUS_IDS.PAYMENT_INVOICED:
      return "Invoiced";
    default:
      return "Unknown";
  }
}

interface BookingsCalendarProps {
  venueId: string;
  portalType: "superadmin" | "booking";
  dataMode?: "server" | "client"; // server = initial data provided, client = fetch via API
  venueDefaultLanguage?: string; // Venue's default language for time formatting
  initialData?: {
    bookings?: any[];
    events?: any[];
    resources?: any[];
    studios?: any[];
    shows?: any[];
    venueName?: string;
    venueCurrency?: string;
  };
}

export default function BookingsCalendar({
  venueId,
  portalType = "superadmin",
  dataMode = "client",
  venueDefaultLanguage = "en",
  initialData,
}: BookingsCalendarProps) {
  const router = useRouter();
  // Use venue's default language for time formatting (24h for Nordic languages)
  const locale = venueDefaultLanguage;

  // Modal state removed - Road Show now handled in booking-new page

  // Data state
  const [events, setEvents] = useState<any[]>(initialData?.events || []);
  const [resources, setResources] = useState<any[]>(
    initialData?.resources || [],
  );
  const [venue, setVenue] = useState<{
    id: number;
    name: string;
    currency: string;
  } | null>(
    initialData?.venueName
      ? {
          id: parseInt(venueId),
          name: initialData.venueName,
          currency: initialData.venueCurrency || "SEK",
        }
      : null,
  );
  const [rawBookings, setRawBookings] = useState<any[]>(
    initialData?.bookings || [],
  );
  const [venueDefaultLang, setVenueDefaultLang] = useState<string>(venueDefaultLanguage); // Use prop value

  // Track current calendar view date range
  // Initialize with appropriate date range based on view type
  const getInitialDateRangeForView = (viewType: string) => {
    const now = new Date();
    let start: Date;
    let end: Date;

    if (viewType === "dayGridMonth") {
      // Month view: current month from 1st to last day
      start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
      end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
    } else if (viewType === "timeGridWeek") {
      // Week view: current week (Monday to Sunday)
      const dayOfWeek = now.getDay();
      const mondayOffset = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Sunday = 0, so offset by 6 days back to Monday
      start = new Date(now);
      start.setDate(now.getDate() - mondayOffset);
      start.setHours(0, 0, 0, 0);

      end = new Date(start);
      end.setDate(start.getDate() + 6);
      end.setHours(23, 59, 59, 999);
    } else if (viewType === "timeGridDay") {
      // Day view: today only
      start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
      end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
    } else {
      // Fallback to month view
      start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
      end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
    }

    return {
      start: start.toISOString(),
      end: end.toISOString(),
    };
  };

  const [currentDateRange, setCurrentDateRange] = useState<{
    start: string;
    end: string;
  }>(() => getInitialDateRangeForView("dayGridMonth")); // Initial view is dayGridMonth
  // fetchBookings is memoised without currentDateRange in its deps (adding it
  // would re-create the callback and re-arm the polling effect on every
  // navigation). Read the range through a ref instead, so a refresh after
  // navigating to another month fetches the month on screen, not the month
  // the page loaded on - otherwise the response replaces the events and the
  // visible month goes blank.
  const currentDateRangeRef = useRef(currentDateRange);
  currentDateRangeRef.current = currentDateRange;
  const [blockedSlots, setBlockedSlots] = useState<any[]>([]);

  // Block dialog state
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [blockDialogData, setBlockDialogData] = useState<{
    studioId: string;
    studioIds?: string[];
    studioName: string;
    studioNames?: string;
    start: Date;
    end: Date;
    date: string;
    startTime: string;
    endTime: string;
    allStudioIds?: string[];
  } | null>(null);
  const [blockReason, setBlockReason] = useState("Blocked");
  const [blockAllStudios, setBlockAllStudios] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [removeDialogData, setRemoveDialogData] = useState<{
    blockId: number;
    studioName: string;
    date: string;
    startTime: string;
    endTime: string;
    reason: string;
  } | null>(null);

  // Loading and error state
  // Start with loading=false to allow FullCalendar to render immediately
  // The refreshing overlay will show a nice loading state while data fetches
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isFetchingRef = useRef(false);  // Prevent concurrent fetches

  // Filter state - persisted in sessionStorage so navigating into a booking
  // detail and back keeps the user's filter selections.
  // Default to Draft, Preliminary, Confirmed (Reserved), and Completed
  // (excludes Cancelled and No-show by default).
  const filtersStorageKey = `bookings-filters:${portalType}:${venueId}`;
  const persistedFilters = useMemo(() => {
    if (typeof window === "undefined") return null;
    try {
      const raw = sessionStorage.getItem(filtersStorageKey);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
    // Read once on first render; we only want to hydrate, not re-read.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [filterStatus, setFilterStatus] = useState<number[]>(
    Array.isArray(persistedFilters?.filterStatus)
      ? persistedFilters.filterStatus
      : [
          STATUS_IDS.BOOKING_DRAFT,      // 46
          STATUS_IDS.BOOKING_PRELIMINARY, // 15
          STATUS_IDS.BOOKING_CONFIRMED,   // 16
          STATUS_IDS.BOOKING_COMPLETED,   // 18
        ],
  );
  const [filterStudio, setFilterStudio] = useState<number[]>(
    Array.isArray(persistedFilters?.filterStudio)
      ? persistedFilters.filterStudio
      : [],
  );
  const [filterHost, setFilterHost] = useState<number[]>(
    Array.isArray(persistedFilters?.filterHost)
      ? persistedFilters.filterHost
      : [],
  );
  const [filterPaymentStatus, setFilterPaymentStatus] = useState<number[]>(
    Array.isArray(persistedFilters?.filterPaymentStatus)
      ? persistedFilters.filterPaymentStatus
      : [],
  );
  const [filterShow, setFilterShow] = useState<number[]>(
    Array.isArray(persistedFilters?.filterShow)
      ? persistedFilters.filterShow
      : [],
  );

  // Search state
  const [searchQuery, setSearchQuery] = useState<string>(
    typeof persistedFilters?.searchQuery === "string"
      ? persistedFilters.searchQuery
      : "",
  );

  // Persist filters whenever they change so they survive navigation.
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      sessionStorage.setItem(
        filtersStorageKey,
        JSON.stringify({
          filterStatus,
          filterStudio,
          filterHost,
          filterPaymentStatus,
          filterShow,
          searchQuery,
        }),
      );
    } catch {
      // ignore quota / private-mode errors
    }
  }, [
    filtersStorageKey,
    filterStatus,
    filterStudio,
    filterHost,
    filterPaymentStatus,
    filterShow,
    searchQuery,
  ]);

  // Real-time polling disabled - use manual refresh button instead
  // Auto-polling every 30 seconds was wasteful with 1000+ records per call
  const [isPolling, setIsPolling] = useState(false);
  const pollingInterval = useRef<NodeJS.Timeout | null>(null);
  const lastFetchTime = useRef<number>(0);

  // Hover state
  const [hoveredEvent, setHoveredEvent] = useState<any>(null);
  const [hoverPosition, setHoverPosition] = useState({ x: 0, y: 0 });
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hoverCardRef = useRef<HTMLDivElement | null>(null);
  const [hoverCardStyle, setHoverCardStyle] = useState<{
    left: number;
    top: number;
  } | null>(null);
  const [viewportVersion, setViewportVersion] = useState(0);

  useEffect(() => {
    const handleResize = () => {
      setViewportVersion((version) => version + 1);
    };
    const handleScroll = () => {
      setHoveredEvent(null); // Hide hover card on scroll
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);
    window.addEventListener("scroll", handleScroll, true); // Capture phase to catch all scrolls

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, []);

  useEffect(() => {
    if (!hoveredEvent || !hoverCardRef.current) return;

    const cardRect = hoverCardRef.current.getBoundingClientRect();
    const padding = 12;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const maxLeft = Math.max(padding, viewportWidth - cardRect.width - padding);
    const nextLeft = Math.min(
      Math.max(hoverPosition.x - cardRect.width / 2, padding),
      maxLeft,
    );

    const spaceAbove = hoverPosition.y - padding;
    const canShowAbove = spaceAbove >= cardRect.height + 10;
    let nextTop = canShowAbove
      ? hoverPosition.y - cardRect.height - 10
      : hoverPosition.y + 10;
    if (nextTop < padding) nextTop = padding;
    if (nextTop + cardRect.height > viewportHeight - padding) {
      nextTop = Math.max(padding, viewportHeight - cardRect.height - padding);
    }

    setHoverCardStyle({ left: nextLeft, top: nextTop });
  }, [hoveredEvent, hoverPosition, viewportVersion]);

  // Convert server-side data format to client-side format
  const normalizeBookingData = useCallback(
    (bookings: any[]): { events: any[]; resources: any[] } => {
      if (!bookings || bookings.length === 0)
        return { events: [], resources: [] };

      // Check if data is already in client format (has statusId)
      if (bookings[0]?.statusId !== undefined) {
        return {
          events: bookings.map((b) => ({
              id: b.id,
              title: b.title,
              start: b.start,
              end: b.end,
              resourceId: b.resourceId,
            backgroundColor: getEventColor(
              b.extendedProps?.statusId || b.statusId,
              b.extendedProps?.paymentStatusId,
            ),
            borderColor: getEventBorderColor(
              b.extendedProps?.statusId || b.statusId,
              b.extendedProps?.paymentStatusId,
            ),
            textColor: getEventTextColor(
              b.extendedProps?.statusId || b.statusId,
              b.extendedProps?.paymentStatusId,
            ),
            extendedProps: {
              ...b.extendedProps,
              // Ensure time strings are preserved
              startTime: b.extendedProps?.startTime,
              endTime: b.extendedProps?.endTime,
              bookingDate: b.extendedProps?.bookingDate,
            },
          })),
          resources: [], // Resources should be provided separately
        };
      }

      // Convert server format to client format
      const events = bookings.map((booking) => {
        const startDateTime = `${booking.booking_date}T${booking.start_time}`;

        // The server has already computed end_time using custom_duration_minutes
        // (per-booking override) → shows.duration_minutes (canonical) → 60 fallback.
        // Do NOT recompute from shows.duration_minutes here — that would silently
        // override per-booking custom durations and produce wrong-length blocks.
        let endDateTime = `${booking.booking_date}T${booking.end_time}`;

        // Road shows still derive end time from the special_request payload.
        if (booking.special_request?.startsWith("Road Show Booking")) {
          const timeMatch = booking.special_request.match(/Time:\s*(\d{2}:\d{2})\s*-\s*(\d{2}:\d{2})/);
          if (timeMatch && timeMatch[2]) {
            endDateTime = `${booking.booking_date}T${timeMatch[2]}:00`;
          }
        }

        return {
          id: booking.id.toString(),
          title:
            booking.customers?.company_name ||
            `${booking.customers?.first_name || ""} ${booking.customers?.last_name || ""}`.trim() ||
            "Unknown Customer",
          start: startDateTime,
          end: endDateTime,
          resourceId: booking.studio_id?.toString(),
          backgroundColor: getEventColor(
            booking.status_id || booking.booking_statuses?.id,
            booking.payment_status_id,
          ),
          borderColor: getEventBorderColor(
            booking.status_id || booking.booking_statuses?.id,
            booking.payment_status_id,
          ),
          textColor: getEventTextColor(
            booking.status_id || booking.booking_statuses?.id,
            booking.payment_status_id,
          ),
          extendedProps: {
            bookingNumber: booking.booking_number,
            showName: booking.shows?.name || "No show",
            showId: booking.show_id,
            participantCount: booking.participant_count,
            statusId: booking.status_id || booking.booking_statuses?.id,
            statusName: booking.booking_statuses?.name,
            paymentStatusId: booking.payment_status_id,
            paymentStatusName:
              booking.payment_status ||
              getPaymentStatusName(booking.payment_status_id),
            customerName: (() => {
              const personName = `${booking.customers?.first_name || ""} ${booking.customers?.last_name || ""}`.trim();
              const companyName = booking.customers?.company_name;
              if (personName && companyName) {
                return `${personName} - ${companyName}`;
              }
              return companyName || personName || "Unknown Customer";
            })(),
            companyName: booking.customers?.company_name || null,
            customerId: booking.customer_id,
            studioName: booking.studios?.name || "Unknown studio",
            studioId: booking.studio_id,
            specialRequest: booking.special_request,  // Use special_request field from bookings table
            bookingType: booking.booking_type,
            bookingTypeId: booking.booking_type_id,
            // Add time fields for display in month view
            startTime: booking.start_time, // Preserve original time string
            endTime: booking.end_time, // Preserve original time string
            bookingDate: booking.booking_date, // Preserve original date
            totalPrice: booking.total_price,
          },
        };
      });

      return { events, resources: [] };
    },
    [],
  );

  // Fetch blocked slots
  const fetchBlockedSlots = useCallback(
    async () => {
      if (dataMode !== "client") return;

      try {
        const response = await fetch(
          `/api/staff/venues/${venueId}/studio-blocks`
        );

        if (!response.ok) {
          console.error("Failed to fetch blocked slots");
          return;
        }

        const data = await response.json();

        if (data.success) {
          // Convert blocked slots to calendar events
          const blockEvents = (data.data || []).map((block: any) => ({
            id: `block-${block.id}`,
            title: "BLOCKED",
            start: `${block.date_from}T${block.time_from || "00:00:00"}`,
            end: `${block.date_to}T${block.time_to || "23:59:59"}`,
            resourceId: block.studio_id?.toString(),
            backgroundColor: "rgba(239, 68, 68, 0.3)", // Low opacity red
            borderColor: "rgba(220, 38, 38, 0.5)",
            textColor: "#dc2626",
            display: "auto", // Changed from "background" to show text
            extendedProps: {
              isBlock: true,
              blockId: block.id,
              reason: block.reason,
            },
          }));

          setBlockedSlots(blockEvents);
        }
      } catch (error) {
        console.error("Error fetching blocked slots:", error);
      }
    },
    [venueId, dataMode],
  );

  // Fetch bookings data (client mode only)
  const fetchBookings = useCallback(
    async (showLoading = false, dateRange?: { start: string; end: string }) => {
      if (dataMode !== "client") return;

      // Prevent concurrent fetches
      if (isFetchingRef.current) return;
      isFetchingRef.current = true;

      try {
        if (showLoading) setLoading(true);
        else setRefreshing(true);

        setError(null);

        // Use date range if provided, otherwise the range currently on screen
        const range = dateRange || currentDateRangeRef.current;

        // Build URL with date parameters if available
        let apiUrl = `/api/staff/venues/${venueId}/bookings`;
        if (range) {
          const params = new URLSearchParams({
            start: range.start,
            end: range.end,
          });
          apiUrl = `${apiUrl}?${params.toString()}`;
        }

        const response = await fetch(apiUrl);

        if (!response.ok) {
          const errorData = await response
            .json()
            .catch(() => ({ error: "Unknown error" }));
          throw new Error(
            errorData.error || `Failed to fetch bookings: ${response.status}`,
          );
        }

        const data = await response.json();

        if (data.success) {
          setRawBookings(data.data.bookings || []);
          setEvents(data.data.events || []);
          setResources(data.data.resources || []);
          // Store venue's default language
          if (data.data.venueDefaultLanguage) {
            setVenueDefaultLang(data.data.venueDefaultLanguage);
          }

          lastFetchTime.current = Date.now();
        } else {
          throw new Error(data.error || "Failed to fetch bookings");
        }
      } catch (error) {
        console.error("Error fetching bookings:", error);
        setError(
          error instanceof Error ? error.message : "Failed to fetch bookings",
        );
        toast.error("Failed to fetch bookings");
      } finally {
        setLoading(false);
        setRefreshing(false);
        isFetchingRef.current = false;
      }
    },
    [venueId, dataMode, portalType],
  );

  // Process initial data for server mode
  useEffect(() => {
    if (dataMode === "server" && initialData?.bookings) {
      const { events: normalizedEvents } = normalizeBookingData(
        initialData.bookings,
      );
      setEvents(normalizedEvents);
      setRawBookings(initialData.bookings);

      // Create resources from studios
      if (initialData.studios) {
        const studioResources = initialData.studios.map((studio) => ({
          id: studio.id.toString(),
          title: studio.name,
          eventColor: "#3b82f6",
        }));
        setResources(studioResources);
      }
    }
  }, [dataMode, initialData, normalizeBookingData]);

  // Fetch blocked slots on mount (client mode only)
  // datesSet fires before this, so currentDateRange is already set
  useEffect(() => {
    if (dataMode === "client") {
      fetchBlockedSlots();
    }
  }, [dataMode, fetchBlockedSlots]);

  // Fetch initial bookings on mount (client mode only)
  // This ensures bookings are loaded when the page first loads with the default date range
  useEffect(() => {
    if (dataMode === "client" && currentDateRange && rawBookings.length === 0) {
      fetchBookings(true, currentDateRange);
    }
  }, [dataMode]); // Only run on mount, not on every currentDateRange change

  // Real-time polling setup (client mode only)
  useEffect(() => {
    if (!isPolling || dataMode !== "client") return;

    const startPolling = () => {
      pollingInterval.current = setInterval(() => {
        if (Date.now() - lastFetchTime.current > 30000) {
          fetchBookings(false);
        }
      }, 30000);
    };

    startPolling();

    return () => {
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
      }
    };
  }, [isPolling, fetchBookings, dataMode]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
      }
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  // Removed automatic refresh on tab switching
  // This was causing stale date range issues and unnecessary API calls
  // Users can manually refresh with the Refresh button if needed

  // Manual refresh
  const handleRefresh = () => {
    if (dataMode === "client") {
      // For day view, ensure we use extended date range to include evening events
      if (currentView === "resourceTimeGridDay" && currentDateRange) {
        const extendedRange = {
          start: currentDateRange.start,
          end: new Date(new Date(currentDateRange.end).getTime() + 12 * 60 * 60 * 1000).toISOString()
        };
        fetchBookings(false, extendedRange);
      } else {
        fetchBookings(false);
      }
      fetchBlockedSlots();
    }
  };

  // Filter handlers
  const handleFilterChange =
    (filterType: string) => (value: string | number) => {
      const numValue = typeof value === "string" ? parseInt(value) : value;

      switch (filterType) {
        case "status":
          setFilterStatus((prev) =>
            prev.includes(numValue)
              ? prev.filter((v) => v !== numValue)
              : [...prev, numValue],
          );
          break;
        case "studio":
          setFilterStudio((prev) =>
            prev.includes(numValue)
              ? prev.filter((v) => v !== numValue)
              : [...prev, numValue],
          );
          break;
        case "host":
          setFilterHost((prev) =>
            prev.includes(numValue)
              ? prev.filter((v) => v !== numValue)
              : [...prev, numValue],
          );
          break;
        case "paymentStatus":
          setFilterPaymentStatus((prev) =>
            prev.includes(numValue)
              ? prev.filter((v) => v !== numValue)
              : [...prev, numValue],
          );
          break;
        case "show":
          setFilterShow((prev) =>
            prev.includes(numValue)
              ? prev.filter((v) => v !== numValue)
              : [...prev, numValue],
          );
          break;
      }
    };

  const handleClearFilters = () => {
    // Reset to default filter state (Draft, Preliminary, Confirmed, Completed - excluding Cancelled and No-show)
    setFilterStatus([
      STATUS_IDS.BOOKING_DRAFT,      // 46
      STATUS_IDS.BOOKING_PRELIMINARY, // 15
      STATUS_IDS.BOOKING_CONFIRMED,   // 16
      STATUS_IDS.BOOKING_COMPLETED,   // 18
    ]);
    setFilterStudio([]);
    setFilterHost([]);
    setFilterPaymentStatus([]);
    setFilterShow([]);
    setSearchQuery("");
  };

  // Prepare filter options from raw bookings
  const filterOptions = useMemo(() => {
    if (!rawBookings || rawBookings.length === 0) {
      return {
        statusOptions: [],
        studioOptions: [],
        hostOptions: [],
        paymentStatusOptions: [],
        showOptions: [],
      };
    }

    // Extract unique values for each filter
    const statuses = new Map<number, { label: string; count: number }>();
    const studios = new Map<number, { label: string; count: number }>();
    const hosts = new Map<number, { label: string; count: number }>();
    const paymentStatuses = new Map<number, { label: string; count: number }>();
    const shows = new Map<number, { label: string; count: number }>();

    rawBookings.forEach((booking) => {
      // Handle both data formats
      const statusId =
        booking.statusId || booking.status_id || booking.booking_statuses?.id;
      const statusName = booking.statusName || booking.booking_statuses?.name;
      const studioId = booking.studioId || booking.studio_id;
      const studioName = booking.studioName || booking.studios?.name;
      const customerId = booking.customerId || booking.customer_id;
      const customerName =
        booking.customerName ||
        booking.customers?.company_name ||
        `${booking.customers?.first_name || ""} ${booking.customers?.last_name || ""}`.trim();
      const showId = booking.showId || booking.show_id;
      const showName = booking.showName || booking.shows?.name;
      const paymentStatusId =
        booking.paymentStatusId || booking.payment_status_id;
      const paymentStatusName =
        booking.paymentStatusName ||
        booking.payment_status ||
        getPaymentStatusName(paymentStatusId);

      // Status
      if (statusId && statusName) {
        statuses.set(statusId, {
          label: statusName,
          count: (statuses.get(statusId)?.count || 0) + 1,
        });
      }

      // Studio
      if (studioId && studioName) {
        studios.set(studioId, {
          label: studioName,
          count: (studios.get(studioId)?.count || 0) + 1,
        });
      }

      // Host (Entertainer/Staff assigned to booking)
      const hostId = booking.hostUserId;
      const hostName =
        booking.hostName ||
        booking.hostUserName ||
        `${booking.hostFirstName || ""} ${booking.hostLastName || ""}`.trim();
      if (hostId && hostName) {
        hosts.set(hostId, {
          label: hostName,
          count: (hosts.get(hostId)?.count || 0) + 1,
        });
      }

      // Payment Status
      if (paymentStatusId && paymentStatusName) {
        paymentStatuses.set(paymentStatusId, {
          label: paymentStatusName,
          count: (paymentStatuses.get(paymentStatusId)?.count || 0) + 1,
        });
      }

      // Show
      if (showId && showName) {
        shows.set(showId, {
          label: showName,
          count: (shows.get(showId)?.count || 0) + 1,
        });
      }
    });

    return {
      statusOptions: Array.from(statuses.entries()).map(([id, data]) => ({
        id,
        label: data.label,
        count: data.count,
      })),
      studioOptions: Array.from(studios.entries())
        .map(([id, data]) => ({
          id,
          label: data.label,
          count: data.count,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
      hostOptions: Array.from(hosts.entries())
        .map(([id, data]) => ({
          id,
          label: data.label,
          count: data.count,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
      paymentStatusOptions: Array.from(paymentStatuses.entries()).map(
        ([id, data]) => ({
          id,
          label: data.label,
          count: data.count,
        }),
      ),
      showOptions: Array.from(shows.entries())
        .map(([id, data]) => ({
          id,
          label: data.label,
          count: data.count,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    };
  }, [rawBookings]);

  // Track current view
  const [currentView, setCurrentView] = useState("dayGridMonth");

  // Month view caps each day at 3 bookings (the rest hide behind a "+N more"
  // popover). When the user turns "Show all" on, we drop that cap so days grow
  // vertically and every booking in the month is visible at a glance. Only
  // affects month view; ignored by the time-grid week/day views.
  const [showAllEvents, setShowAllEvents] = useState(false);

  // Ref to the FullCalendar instance so the date picker can navigate
  // programmatically (gotoDate / changeView) without clicking prev/next.
  const calendarRef = useRef<FullCalendar | null>(null);

  // Format a Date as the YYYY-MM-DD value an <input type="date"> expects,
  // using LOCAL parts (not toISOString, which would shift across timezones).
  const toDateInputValue = (d: Date) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // The day shown in the date picker. Kept in sync with the calendar as the
  // user navigates (see datesSet), and drives jump-to-day on change.
  const [pickerDate, setPickerDate] = useState<string>(() =>
    toDateInputValue(new Date()),
  );

  // Jump straight to a specific day in day view. Parse the value as a LOCAL
  // date (new Date("YYYY-MM-DD") is parsed as UTC and can land on the wrong
  // day in negative-offset timezones).
  const handleDatePick = (value: string) => {
    if (!value) return;
    const [year, month, day] = value.split("-").map(Number);
    if (!year || !month || !day) return;
    const target = new Date(year, month - 1, day);

    const api = calendarRef.current?.getApi();
    if (api) {
      if (api.view.type === "resourceTimeGridDay") {
        api.gotoDate(target);
      } else {
        // Land on the chosen day in day view, since that's the view the
        // picker is meant to speed up navigating.
        api.changeView("resourceTimeGridDay", target);
      }
    }
    setPickerDate(value);
  };

  // Apply filters to events
  const filteredEvents = useMemo(() => {
    // Only include blocked slots in resourceTimeGridDay view
    const eventsToShow = currentView === "resourceTimeGridDay"
      ? [...(events || []), ...(blockedSlots || [])]
      : [...(events || [])];

    if (eventsToShow.length === 0) return [];


    return eventsToShow.filter((event) => {
      // Don't filter blocked slots (they're already filtered by view)
      if (event.extendedProps?.isBlock) return true;
      const props = event.extendedProps;

      // Status filter
      if (filterStatus.length > 0 && !filterStatus.includes(props.statusId)) {
        return false;
      }

      // Studio filter
      if (filterStudio.length > 0 && !filterStudio.includes(props.studioId)) {
        return false;
      }

      // Host filter
      if (filterHost.length > 0 && !filterHost.includes(props.hostUserId)) {
        return false;
      }

      // Payment status filter
      if (
        filterPaymentStatus.length > 0 &&
        !filterPaymentStatus.includes(props.paymentStatusId)
      ) {
        return false;
      }

      // Show filter
      if (filterShow.length > 0 && !filterShow.includes(props.showId)) {
        return false;
      }

      return true;
    });
  }, [
    events,
    blockedSlots,
    currentView,
    filterStatus,
    filterStudio,
    filterHost,
    filterPaymentStatus,
    filterShow,
  ]);

  // Apply filters to resources (studios)
  const filteredResources = useMemo(() => {
    // If no resources, create a default one to show the resource view structure
    if (!resources || resources.length === 0) {
      return [
        {
          id: "no-studios",
          title: "No studios configured",
        },
      ];
    }

    // If studio filter is active, only show selected studios
    if (filterStudio.length > 0) {
      return resources.filter((resource) =>
        filterStudio.includes(parseInt(resource.id)),
      );
    }

    return resources;
  }, [resources, filterStudio]);

  const handleDateClick = (arg: any) => {
    // Check if we're in resource view (day view with studios)
    if (arg.resource || currentView === "resourceTimeGridDay") {
      // In resource view, don't navigate, let select handle it
      return;
    }
    // Navigate to new booking page with the selected day. Week view gives a
    // timestamp like "2026-10-05T11:00:00+02:00"; its "+" turns into a space
    // in the query string and the page could not read it, so pass the day only.
    const day = arg.dateStr.slice(0, 10);
    const newBookingPath =
      portalType === "superadmin"
        ? `/superadmin/venue/${venueId}/booking-new?date=${day}`
        : `/booking/venue/${venueId}/booking-new?date=${day}`;
    router.push(newBookingPath);
  };

  const handleSlotSelect = (arg: any) => {
    // Only handle selection in resource day view
    if (currentView !== "resourceTimeGridDay" || !arg.resource) {
      arg.view.calendar.unselect();
      return;
    }

    const studioId = arg.resource.id;
    const start = arg.start;
    const end = arg.end;

    // Don't create blocks for "no-studios" placeholder
    if (studioId === "no-studios") {
      toast.error("Please configure studios first");
      arg.view.calendar.unselect();
      return;
    }

    // Check if this time slot is already blocked for this studio
    const isAlreadyBlocked = blockedSlots?.some((block: any) => {
      const blockStart = new Date(block.start);
      const blockEnd = new Date(block.end);
      const blockStudioId = block.resourceId;

      // Check for overlap with existing blocks
      return blockStudioId === studioId &&
             ((start >= blockStart && start < blockEnd) ||
              (end > blockStart && end <= blockEnd) ||
              (start <= blockStart && end >= blockEnd));
    });

    if (isAlreadyBlocked) {
      toast.error("This time slot is already blocked for this studio");
      arg.view.calendar.unselect();
      return;
    }

    // Format the time for display using locale-aware formatting
    const startTime = formatTimeDisplay(
      start.toTimeString().slice(0, 5),
      locale
    );
    const endTime = formatTimeDisplay(
      end.toTimeString().slice(0, 5),
      locale
    );
    const date = start.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });

    // Get studio name
    const studioName = resources.find(r => r.id === studioId)?.title || "Studio";

    // Option to block multiple studios with same time slot
    const allStudioIds = resources.filter(r => r.id !== "no-studios").map(r => r.id);

    // Set dialog data and open dialog
    setBlockDialogData({
      studioId,
      studioIds: [studioId], // Single studio by default
      studioName,
      studioNames: studioName,
      start,
      end,
      date,
      startTime,
      endTime,
      allStudioIds, // Pass all studio IDs for optional "block all" feature
    });
    setBlockReason("Blocked"); // Reset reason
    setBlockAllStudios(false); // Reset block all checkbox
    setBlockDialogOpen(true);

    // Clear the selection
    arg.view.calendar.unselect();
  };

  const handleBlockConfirm = async () => {
    if (!blockDialogData) return;

    try {
      // Determine which studios to block
      let studiosToBlock = [];
      if (blockAllStudios && blockDialogData.allStudioIds) {
        // Block all studios
        studiosToBlock = blockDialogData.allStudioIds;
      } else {
        // Block only selected studio(s)
        studiosToBlock = blockDialogData.studioIds || [blockDialogData.studioId];
      }

      // Filter out already blocked studios if blocking multiple
      if (studiosToBlock.length > 1) {
        studiosToBlock = studiosToBlock.filter((studioId: string) => {
          const isBlocked = blockedSlots?.some((block: any) => {
            const blockStart = new Date(block.start);
            const blockEnd = new Date(block.end);
            const blockStudioId = block.resourceId;

            return blockStudioId === studioId &&
                   ((blockDialogData.start >= blockStart && blockDialogData.start < blockEnd) ||
                    (blockDialogData.end > blockStart && blockDialogData.end <= blockEnd) ||
                    (blockDialogData.start <= blockStart && blockDialogData.end >= blockEnd));
          });
          return !isBlocked;
        });

        if (studiosToBlock.length === 0) {
          toast.info("All selected studios are already blocked for this time slot");
          setBlockDialogOpen(false);
          setBlockAllStudios(false);
          return;
        }
      }

      // Create blocks for all selected studios
      const blockPromises = studiosToBlock.map((studioId: string) => {
        // Format dates and times in local timezone for the backend
        // We need to send the actual selected local time, not UTC
        const startDate = blockDialogData.start;
        const endDate = blockDialogData.end;

        // Format date as YYYY-MM-DD in local timezone
        const formatLocalDate = (date: Date) => {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        };

        // Format time as HH:MM:SS in local timezone
        const formatLocalTime = (date: Date) => {
          const hours = String(date.getHours()).padStart(2, '0');
          const minutes = String(date.getMinutes()).padStart(2, '0');
          const seconds = String(date.getSeconds()).padStart(2, '0');
          return `${hours}:${minutes}:${seconds}`;
        };

        // Create a local datetime string that preserves the selected time
        const startLocal = `${formatLocalDate(startDate)}T${formatLocalTime(startDate)}`;
        const endLocal = `${formatLocalDate(endDate)}T${formatLocalTime(endDate)}`;

        return fetch(`/api/staff/venues/${venueId}/studio-blocks`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            studioId: studioId,
            start: startLocal,
            end: endLocal,
            reason: blockReason || "Blocked",
          }),
        });
      });

      const responses = await Promise.all(blockPromises);
      const successfulCount = responses.filter(r => r.ok).length;
      const failedCount = responses.filter(r => r.status === 409).length; // 409 = Conflict (already blocked)

      if (successfulCount > 0) {
        toast.success(
          successfulCount > 1
            ? `Time slot blocked for ${successfulCount} studios`
            : "Time slot blocked successfully"
        );
        if (failedCount > 0) {
          toast.info(`${failedCount} studio(s) were already blocked`);
        }
        // Refresh to show the new blocks
        // For day view, ensure we use extended date range to include evening events
        if (currentView === "resourceTimeGridDay" && currentDateRange) {
          const extendedRange = {
            start: currentDateRange.start,
            end: new Date(new Date(currentDateRange.end).getTime() + 12 * 60 * 60 * 1000).toISOString()
          };
          fetchBookings(false, extendedRange);
        } else {
          fetchBookings(false);
        }
        fetchBlockedSlots();
        setBlockDialogOpen(false);
        setBlockAllStudios(false);
      } else if (failedCount > 0) {
        toast.info("All selected studios were already blocked for this time slot");
        setBlockDialogOpen(false);
        setBlockAllStudios(false);
      } else {
        toast.error("Failed to create blocks");
      }
    } catch (error) {
      console.error("Error creating blocks:", error);
      toast.error("Failed to create blocks");
    }
  };

  const handleEventClick = (arg: any) => {
    // Check if this is a blocked slot
    if (arg.event.extendedProps?.isBlock) {
      const startTime = formatTimeDisplay(
        arg.event.start.toTimeString().slice(0, 5),
        locale
      );
      const endTime = formatTimeDisplay(
        arg.event.end.toTimeString().slice(0, 5),
        locale
      );
      const date = arg.event.start.toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
      });

      // Get studio name from resource
      const studioName = resources.find(r => r.id === arg.event._def.resourceIds[0])?.title || "Studio";

      // Set remove dialog data and open dialog
      setRemoveDialogData({
        blockId: arg.event.extendedProps.blockId,
        studioName,
        date,
        startTime,
        endTime,
        reason: arg.event.extendedProps.reason,
      });
      setRemoveDialogOpen(true);
      return;
    }

    // Extract the actual booking ID (event ID might be "bookingId-timeslot" for multi-show bookings)
    const eventId = arg.event.id;
    const bookingId = eventId.split('-')[0]; // Get the first part before the hyphen
    // Open the booking details page in a new tab so staff can keep multiple
    // bookings open side-by-side (e.g. while invoicing).
    const url = `${PORTAL_ROUTES[portalType].venue(venueId).bookings}/${bookingId}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleRemoveConfirm = async () => {
    if (!removeDialogData) return;

    try {
      const response = await fetch(
        `/api/staff/venues/${venueId}/studio-blocks?id=${removeDialogData.blockId}`,
        {
          method: "DELETE",
        }
      );

      if (response.ok) {
        toast.success("Block removed successfully");
        // Refresh to show the change
        // For day view, ensure we use extended date range to include evening events
        if (currentView === "resourceTimeGridDay" && currentDateRange) {
          const extendedRange = {
            start: currentDateRange.start,
            end: new Date(new Date(currentDateRange.end).getTime() + 12 * 60 * 60 * 1000).toISOString()
          };
          fetchBookings(false, extendedRange);
        } else {
          fetchBookings(false);
        }
        fetchBlockedSlots();
        setRemoveDialogOpen(false);
      } else {
        const error = await response.json();
        toast.error(error.error || "Failed to remove block");
      }
    } catch (error) {
      console.error("Error removing block:", error);
      toast.error("Failed to remove block");
    }
  };

  const handleEventDrop = (arg: any) => {
    // Event dropped: arg.event.title, New date: arg.event.start
    // TODO: Implement booking update API call
  };

  const handleEventResize = (arg: any) => {
    // Event resized: arg.event.title
    // TODO: Implement booking update API call
  };

  if (loading) {
    return (
      <div className="container py-8">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">Loading bookings...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-8">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <p className="text-red-600 mb-4">Error: {error}</p>
            <Button onClick={() => fetchBookings(true)}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-6 py-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Bookings</h1>
              <p className="text-gray-600 mt-2">
                Manage your venue bookings and events
              </p>
            </div>
            <div className="flex gap-2 items-center">
              {/* Jump-to-day picker — go straight to a date in day view
                  instead of clicking prev/next repeatedly. */}
              <div className="relative">
                <Calendar className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                <Input
                  type="date"
                  aria-label="Go to day"
                  title="Go to a specific day"
                  value={pickerDate}
                  onChange={(e) => handleDatePick(e.target.value)}
                  className="pl-8 w-[170px]"
                />
              </div>

              {/* Search Dropdown */}
              <SearchDropdown
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                bookings={events}
                resources={resources}
                venueId={venueId}
                onBookingSelect={(bookingId) => {
                  const url = `${PORTAL_ROUTES[portalType].venue(venueId).bookings}/${bookingId}`;
                  window.open(url, "_blank", "noopener,noreferrer");
                }}
                locale={locale}
              />

              {/* Show all — only meaningful in month view, where days are
                  otherwise capped at 3 bookings. Expands every day vertically
                  to reveal all of the month's bookings. */}
              {currentView === "dayGridMonth" && (
                <Button
                  variant={showAllEvents ? "default" : "outline"}
                  onClick={() => setShowAllEvents((prev) => !prev)}
                  aria-pressed={showAllEvents}
                  title={
                    showAllEvents
                      ? "Collapse days back to a compact month grid"
                      : "Expand every day to show all bookings in the month"
                  }
                >
                  <Rows3 className="h-4 w-4 mr-2" />
                  {showAllEvents ? "Show less" : "Show all"}
                </Button>
              )}

              {dataMode === "client" && (
                <Button
                  variant="outline"
                  onClick={handleRefresh}
                  disabled={refreshing}
                >
                  <RefreshCw
                    className={`h-4 w-4 mr-2 ${refreshing ? "animate-spin" : ""}`}
                  />
                  Refresh
                </Button>
              )}
              <Button
                className="bg-primary text-primary-foreground hover:bg-primary/90"
                asChild
              >
                <Link href={`/${portalType}/venue/${venueId}/booking-new`}>
                  <Plus className="h-4 w-4 mr-2" />
                  New booking
                </Link>
              </Button>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border p-4">
            <BookingFilters
              statusOptions={filterOptions.statusOptions}
              studioOptions={filterOptions.studioOptions}
              hostOptions={filterOptions.hostOptions}
              paymentStatusOptions={filterOptions.paymentStatusOptions}
              showOptions={filterOptions.showOptions}
              filterStatus={filterStatus}
              filterStudio={filterStudio}
              filterHost={filterHost}
              filterPaymentStatus={filterPaymentStatus}
              filterShow={filterShow}
              onFilterStatusChange={handleFilterChange("status")}
              onFilterStudioChange={handleFilterChange("studio")}
              onFilterHostChange={handleFilterChange("host")}
              onFilterPaymentStatusChange={handleFilterChange("paymentStatus")}
              onFilterShowChange={handleFilterChange("show")}
              onClearFilters={handleClearFilters}
            />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6 relative">
          {/* Loading overlay for month navigation */}
          {refreshing && (
            <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center rounded-lg">
              <div className="text-center">
                <RefreshCw className="h-10 w-10 animate-spin mx-auto mb-3 text-primary" />
                <p className="text-lg font-medium text-muted-foreground">Loading bookings...</p>
                <p className="text-sm text-muted-foreground mt-1">This may take a moment for busy months</p>
              </div>
            </div>
          )}
          <FullCalendar
            ref={calendarRef}
            schedulerLicenseKey="GPL-My-Project-Is-Open-Source"
            plugins={[
              dayGridPlugin,
              timeGridPlugin,
              interactionPlugin,
              resourceTimeGridPlugin,
            ]}
            initialView="dayGridMonth"
            firstDay={1}  // Monday = 1 (0=Sunday, 1=Monday, 2=Tuesday, etc.)
            headerToolbar={{
              left: "prev,next today",
              center: "title",
              right: "dayGridMonth,timeGridWeek,resourceTimeGridDay",
            }}
            views={{
              resourceTimeGridDay: {
                type: "resourceTimeGridDay",
                buttonText: "Day",
                titleFormat: {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric'
                },
              },
              timeGridDay: {
                titleFormat: {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric'
                },
              },
            }}
            resourceAreaHeaderContent="Studios"
            resourceAreaWidth="150px"
            timeZone="local"
            resources={filteredResources}
            events={filteredEvents}
            height="auto"
            aspectRatio={1.8}
            slotMinTime="06:00:00"
            slotMaxTime="23:59:59"
            slotDuration="00:30:00"
            allDaySlot={false}
            editable={false}
            selectable={true}
            selectMirror={true}
            selectAllow={(selectInfo: any) => {
              // Allow selection only in resource day view
              return currentView === "resourceTimeGridDay";
            }}
            dayMaxEvents={
              currentView === "dayGridMonth" && !showAllEvents ? 3 : false
            }
            weekends={true}
            nowIndicator={true}
            businessHours={{
              daysOfWeek: [1, 2, 3, 4, 5],
              startTime: "09:00",
              endTime: "18:00",
            }}
            eventDisplay="block"
            moreLinkClick="popover"
            dateClick={handleDateClick}
            select={handleSlotSelect}
            eventClick={handleEventClick}
            eventDrop={handleEventDrop}
            eventResize={handleEventResize}
            eventContent={(eventInfo) => {
              const viewType = eventInfo.view.type;
              const isMonthView = viewType === "dayGridMonth";
              const isTimeGridView =
                viewType === "timeGridWeek" || viewType === "timeGridDay" ||
                viewType === "resourceTimeGridDay" || viewType === "resourceTimeGrid";

              // Check if this is a blocked slot
              if (eventInfo.event.extendedProps?.isBlock) {
                return (
                  <div className="p-1 text-xs h-full flex items-center justify-center">
                    <div className="font-bold text-red-700 text-center">
                      BLOCKED
                    </div>
                  </div>
                );
              }

              // Check if booking needs invoice alert
              const showInvoiceAlert =
                eventInfo.event.extendedProps.statusId ===
                  STATUS_IDS.BOOKING_COMPLETED &&
                eventInfo.event.extendedProps.paymentMethod === "invoice" &&
                !eventInfo.event.extendedProps.hasInvoice;

              // Check if this is a road show booking
              const isRoadShow = eventInfo.event.extendedProps.bookingTypeId === BOOKING_TYPE_IDS.ON_ROAD;

              // Check if booking is in a non-native language
              const bookingLanguage = eventInfo.event.extendedProps.showLanguage || 'en';
              const isNonNativeLanguage = bookingLanguage !== venueDefaultLang;

              // Partner-brokered booking — surface a handshake icon so staff
              // can spot partner bookings at a glance.
              const isPartner = !!eventInfo.event.extendedProps.isPartner;

              // Booking has add-ons (some must be pre-ordered) — details in the hover card.
              const hasAddOns = (eventInfo.event.extendedProps.addOns?.length ?? 0) > 0;

              return (
                <div className="p-1 text-xs leading-tight h-full overflow-hidden relative">
                  {/* Position indicators in a row - adjusted top padding to align with show name */}
                  <div className="absolute top-1 right-1 flex items-center gap-1">
                    {/* Language marker for non-native language shows */}
                    {isNonNativeLanguage && (
                      <LanguageFlag language={bookingLanguage} size={14} />
                    )}
                    {/* Add-ons indicator */}
                    {hasAddOns && (
                      <Gift className="w-4 h-4" style={{ color: eventInfo.event.textColor || 'currentColor' }} />
                    )}
                    {/* Partner booking indicator */}
                    {isPartner && (
                      <Handshake className="w-4 h-4" style={{ color: eventInfo.event.textColor || 'currentColor' }} />
                    )}
                    {/* Road show indicator */}
                    {isRoadShow && (
                      <Route className="w-4 h-4" style={{ color: eventInfo.event.textColor || 'currentColor' }} />
                    )}
                    {/* Invoice alert */}
                    {showInvoiceAlert && (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        className="w-4 h-4 text-orange-500"
                      >
                        <path
                          fillRule="evenodd"
                          d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </div>
                  <div
                    className="font-semibold"
                    style={{
                      display: "-webkit-box",
                      WebkitLineClamp: isTimeGridView ? 2 : 1,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      lineHeight: "1.1",
                    }}
                  >
                    {eventInfo.event.extendedProps.showName}
                  </div>
                  <div
                    className="opacity-90 mt-0.5"
                    style={{
                      display: "-webkit-box",
                      WebkitLineClamp: isTimeGridView ? 2 : 1,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      lineHeight: "1.1",
                      marginBottom: isMonthView ? "14px" : "0", // Add margin to prevent overlap with bottom elements
                    }}
                  >
                    {eventInfo.event.extendedProps.customerName}
                  </div>
                  {/* Show participant count in bottom left corner for month view */}
                  {isMonthView && eventInfo.event.extendedProps.participantCount > 0 && (
                    <div className="absolute bottom-0 left-1 text-[10px] font-semibold opacity-90">
                      {eventInfo.event.extendedProps.participantCount} pax
                    </div>
                  )}
                  {/* Show time in bottom right corner for month view */}
                  {isMonthView && eventInfo.event.extendedProps.startTime && (
                    <div className="absolute bottom-0 right-1 text-[10px] font-semibold opacity-90">
                      {formatTimeDisplay(eventInfo.event.extendedProps.startTime, locale)}-{formatTimeDisplay(eventInfo.event.extendedProps.endTime, locale)}
                    </div>
                  )}
                  {/* Show participant count in time grid views */}
                  {!isMonthView && isTimeGridView && eventInfo.event.extendedProps.participantCount > 0 && (
                    <div className="font-medium mt-0.5">
                      {eventInfo.event.extendedProps.participantCount} pax
                    </div>
                  )}
                </div>
              );
            }}
            datesSet={(dateInfo) => {
              // Called when the calendar's date range changes (view change, navigation)
              let startDate = dateInfo.start;
              let endDate = dateInfo.end;

              // Keep the date picker showing the day the calendar is on, so
              // prev/next/today and view changes are reflected in the input.
              const apiDate = calendarRef.current?.getApi().getDate();
              if (apiDate) setPickerDate(toDateInputValue(apiDate));

              // Adjust dates based on view type
              if (dateInfo.view.type === "dayGridMonth") {
                // For month view, get the actual month being viewed (not the 6-week display range)
                // The title contains the month/year being viewed
                const viewTitle = dateInfo.view.title; // e.g., "January 2026"

                // Get the current date in view (center of the range is safest)
                const centerDate = new Date(
                  dateInfo.start.getTime() +
                  (dateInfo.end.getTime() - dateInfo.start.getTime()) / 2
                );

                // Set to first day of the actual month at 00:00:00
                startDate = new Date(centerDate.getFullYear(), centerDate.getMonth(), 1, 0, 0, 0, 0);

                // Set to last day of the actual month at 23:59:59
                endDate = new Date(centerDate.getFullYear(), centerDate.getMonth() + 1, 0, 23, 59, 59, 999);
              }
              // For day view, extend the end time to cover the full day
              if (dateInfo.view.type === "resourceTimeGridDay") {
                // The problem: dateInfo.end is at 7PM UTC, missing evening events
                // Solution: Add extra hours to ensure we get all events for the day
                endDate = new Date(dateInfo.end);
                endDate.setHours(endDate.getHours() + 12); // Add 12 hours to cover until 7AM next day
              }
              // For week view, use the provided date range as-is from dateInfo.start and dateInfo.end

              const newDateRange = {
                start: startDate.toISOString(),
                end: endDate.toISOString(),
              };


              // Only fetch if the date range actually changed
              if (currentDateRange?.start !== newDateRange.start ||
                  currentDateRange?.end !== newDateRange.end) {
                // Update current date range
                setCurrentDateRange(newDateRange);

                // Fetch bookings for new date range (only in client mode)
                if (dataMode === "client") {
                  fetchBookings(false, newDateRange);
                }
              }
            }}
            eventMouseEnter={(info) => {
              info.el.style.cursor = "pointer";

              // Clear any existing timeout
              if (hoverTimeoutRef.current) {
                clearTimeout(hoverTimeoutRef.current);
              }

              // Set a small delay to prevent flickering
              hoverTimeoutRef.current = setTimeout(() => {
                const rect = info.el.getBoundingClientRect();
                setHoverPosition({
                  x: rect.left + rect.width / 2,
                  y: rect.top,
                });

                // For road shows, extract the actual end time from special_request
                let actualEndTime = info.event.extendedProps.endTime;
                if (info.event.extendedProps.specialRequest?.startsWith("Road Show Booking")) {
                  const timeMatch = info.event.extendedProps.specialRequest.match(/Time:\s*(\d{2}:\d{2})\s*-\s*(\d{2}:\d{2})/);
                  if (timeMatch && timeMatch[2]) {
                    actualEndTime = `${timeMatch[2]}:00`;
                  }
                }

                setHoveredEvent({
                  bookingNumber: info.event.extendedProps.bookingNumber,
                  showName: info.event.extendedProps.showName,
                  participantCount: info.event.extendedProps.participantCount,
                  statusId: info.event.extendedProps.statusId,
                  statusName: info.event.extendedProps.statusName,
                  paymentStatusId: info.event.extendedProps.paymentStatusId,
                  paymentStatusName: info.event.extendedProps.paymentStatusName,
                  customerName: info.event.extendedProps.customerName,
                  companyName: info.event.extendedProps.companyName,
                  studioName: info.event.extendedProps.studioName,
                  specialRequest: info.event.extendedProps.specialRequest,  // Use special_request field
                  bookingType: info.event.extendedProps.bookingType,
                  bookingTypeId: info.event.extendedProps.bookingTypeId,
                  eventType: info.event.extendedProps.eventType,
                  totalPrice: info.event.extendedProps.totalPrice,
                  addOns: info.event.extendedProps.addOns || [],
                  start: info.event.start,
                  end: info.event.end,
                  startTime: info.event.extendedProps.startTime, // Use preserved time string
                  endTime: actualEndTime, // Use extracted time for road shows
                  bookingDate: info.event.extendedProps.bookingDate, // Use preserved date
                });
              }, 200); // 200ms delay
            }}
            eventMouseLeave={() => {
              // Clear timeout if mouse leaves before hover shows
              if (hoverTimeoutRef.current) {
                clearTimeout(hoverTimeoutRef.current);
                hoverTimeoutRef.current = null;
              }
              setHoveredEvent(null);
            }}
            viewDidMount={(info) => {
              // Track current view type
              setCurrentView(info.view.type);
            }}
          />
        </div>

        {/* Hover Card */}
        {hoveredEvent && (
          <div
            ref={hoverCardRef}
            className="fixed w-80 max-w-[calc(100vw-24px)] max-h-[calc(100vh-24px)] overflow-y-auto bg-white/95 backdrop-blur border border-gray-200 rounded-lg shadow-2xl pointer-events-none"
            style={{
              left: hoverCardStyle?.left ?? 0,
              top: hoverCardStyle?.top ?? 0,
              visibility: hoverCardStyle ? "visible" : "hidden",
              zIndex: 9999,
            }}
          >
            <div className="p-4 space-y-3">
              {/* Header */}
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-semibold text-lg">
                    {hoveredEvent.bookingNumber}
                  </p>
                  <p className="text-sm text-gray-600">
                    {hoveredEvent.bookingTypeId === BOOKING_TYPE_IDS.ON_ROAD
                      ? "Road show"
                      : hoveredEvent.bookingType === "corporate"
                        ? "Corporate event"
                        : "Private booking"}
                  </p>
                </div>
                <div className="flex flex-col gap-1 items-end">
                  <Badge variant={getStatusBadgeVariant(hoveredEvent.statusId)}>
                    {hoveredEvent.statusName || "Unknown"}
                  </Badge>
                  {hoveredEvent.paymentStatusId && (
                    <Badge
                      variant="outline"
                      className={getPaymentBadgeStyle(
                        hoveredEvent.paymentStatusId,
                      )}
                    >
                      <CreditCard className="h-3 w-3 mr-1" />
                      {hoveredEvent.paymentStatusName ||
                        getPaymentStatusText(hoveredEvent.paymentStatusId)}
                    </Badge>
                  )}
                </div>
              </div>

              {/* Show name always; studio only for on-site (road shows have none) */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Building2 className="h-4 w-4 text-gray-400" />
                  <span className="font-medium">{hoveredEvent.showName}</span>
                </div>
                {hoveredEvent.studioName &&
                  hoveredEvent.studioName !== "Unknown studio" && (
                    <div className="flex items-center gap-2 text-sm">
                      <MapPin className="h-4 w-4 text-gray-400" />
                      <span>{hoveredEvent.studioName}</span>
                    </div>
                  )}
              </div>

              {/* Date & Time */}
              <div className="pt-2 border-t">
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  <span>
                    {new Date(hoveredEvent.start).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                    })}
                  </span>
                  <span className="text-gray-400">•</span>
                  <Clock className="h-4 w-4 text-gray-400" />
                  <span>
                    {hoveredEvent.startTime ? (
                      <>
                        {formatTimeString(hoveredEvent.startTime, locale)} -{" "}
                        {formatTimeString(hoveredEvent.endTime, locale)}
                      </>
                    ) : (
                      <>
                        {formatTimeDisplay(
                          new Date(hoveredEvent.start).toTimeString().slice(0, 5),
                          locale
                        )}{" "}
                        -{" "}
                        {formatTimeDisplay(
                          new Date(hoveredEvent.end).toTimeString().slice(0, 5),
                          locale
                        )}
                      </>
                    )}
                  </span>
                </div>
              </div>

              {/* Customer & Details */}
              <div className="space-y-2 pt-2 border-t">
                <div className="flex items-center gap-2 text-sm">
                  <User className="h-4 w-4 text-gray-400" />
                  <span>{hoveredEvent.customerName}</span>
                </div>
                {/* Company name (corporate customer / partner end customer) */}
                {hoveredEvent.companyName && (
                  <div className="flex items-center gap-2 text-sm">
                    <Building2 className="h-4 w-4 text-gray-400" />
                    <span className="font-medium">
                      {hoveredEvent.companyName}
                    </span>
                  </div>
                )}
                {/* Hide participants for road shows */}
                {hoveredEvent.bookingTypeId !== BOOKING_TYPE_IDS.ON_ROAD && (
                  <div className="flex items-center gap-2 text-sm">
                    <Users className="h-4 w-4 text-gray-400" />
                    <span>{hoveredEvent.participantCount} participants</span>
                  </div>
                )}
              </div>

              {/* Add-ons (customers can change these, and some must be pre-ordered) */}
              {hoveredEvent.addOns?.length > 0 && (
                <div className="border-t pt-3 space-y-1">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <Gift className="h-4 w-4 text-gray-400" />
                    <span>Add-ons</span>
                  </div>
                  <ul className="pl-6 space-y-0.5 text-sm">
                    {hoveredEvent.addOns.map(
                      (addOn: { name: string; quantity: number }) => (
                        <li key={addOn.name} className="flex justify-between gap-2">
                          <span>{addOn.name}</span>
                          <span className="text-gray-500 tabular-nums">
                            × {addOn.quantity}
                          </span>
                        </li>
                      ),
                    )}
                  </ul>
                </div>
              )}

              {/* Notes if any - use special_request field */}
              {hoveredEvent.specialRequest && (
                <div className="border-t pt-3 space-y-1">
                  <p className="text-xs text-muted-foreground">
                    <span className="font-medium">Request:</span>{" "}
                    {hoveredEvent.specialRequest}
                  </p>
                </div>
              )}

              {/* Footer */}
              <div className="text-center pt-2 border-t">
                <p className="text-xs text-muted-foreground">
                  Click to view details
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Block Confirmation Dialog */}
        <Dialog open={blockDialogOpen} onOpenChange={setBlockDialogOpen}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Ban className="h-5 w-5 text-red-500" />
                Block time slot
              </DialogTitle>
              <DialogDescription>
                This will prevent bookings during the selected time period.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Building2 className="h-4 w-4 text-gray-500" />
                  <span className="font-medium">Studio{blockDialogData?.studioIds && blockDialogData.studioIds.length > 1 ? 's' : ''}:</span>
                  <span>{blockDialogData?.studioNames || blockDialogData?.studioName}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="h-4 w-4 text-gray-500" />
                  <span className="font-medium">Date:</span>
                  <span>{blockDialogData?.date}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="h-4 w-4 text-gray-500" />
                  <span className="font-medium">Time:</span>
                  <span>{blockDialogData?.startTime} - {blockDialogData?.endTime}</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="reason" className="text-sm font-medium">
                  Reason for blocking (optional)
                </Label>
                <Input
                  id="reason"
                  placeholder="e.g., Maintenance, cleaning, private event"
                  value={blockReason}
                  onChange={(e) => setBlockReason(e.target.value)}
                />
              </div>
              {/* Show checkbox only when there are multiple studios */}
              {blockDialogData?.allStudioIds && blockDialogData.allStudioIds.length > 1 && (
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="blockAll"
                    checked={blockAllStudios}
                    onCheckedChange={(checked) => setBlockAllStudios(!!checked)}
                  />
                  <label
                    htmlFor="blockAll"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Block this time slot for all {blockDialogData.allStudioIds.length} studios
                  </label>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setBlockDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleBlockConfirm}
                className="bg-red-500 hover:bg-red-600 text-white"
              >
                <Ban className="h-4 w-4 mr-2" />
                Block Slot
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Remove Block Dialog */}
        <Dialog open={removeDialogOpen} onOpenChange={setRemoveDialogOpen}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-orange-500" />
                Remove Block
              </DialogTitle>
              <DialogDescription>
                This will allow bookings during this time period again.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Building2 className="h-4 w-4 text-gray-500" />
                  <span className="font-medium">Studio:</span>
                  <span>{removeDialogData?.studioName}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="h-4 w-4 text-gray-500" />
                  <span className="font-medium">Date:</span>
                  <span>{removeDialogData?.date}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="h-4 w-4 text-gray-500" />
                  <span className="font-medium">Time:</span>
                  <span>{removeDialogData?.startTime} - {removeDialogData?.endTime}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <MessageSquare className="h-4 w-4 text-gray-500" />
                  <span className="font-medium">Reason:</span>
                  <span className="text-gray-600">{removeDialogData?.reason}</span>
                </div>
              </div>
              <div className="bg-orange-50 p-3 rounded-md">
                <p className="text-sm text-orange-800">
                  Are you sure you want to remove this block? The time slot will become available for bookings again.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setRemoveDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleRemoveConfirm}
                variant="destructive"
              >
                Remove Block
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

// Helper functions
function formatTimeString(timeStr: string, locale?: string): string {
  // Handle time strings in format "HH:MM:SS" or "HH:MM"
  if (!timeStr) return "";

  // Use locale-aware formatting from centralized utility
  return formatTimeDisplay(timeStr, locale);
}

function getEventColor(statusId: number, paymentStatusId?: number): string {
  // Check if booking is paid - if so, return black regardless of booking status
  if (paymentStatusId === STATUS_IDS.PAYMENT_PAID) {
    return "#000000"; // Black for paid bookings
  }

  // Check if booking is invoiced - if so, return light purple
  if (paymentStatusId === STATUS_IDS.PAYMENT_INVOICED) {
    return "#c084fc"; // Light purple (purple-400) for invoiced bookings
  }

  // Use centralized color config to get the appropriate color
  const colorConfig = getStatusColorConfig(statusId);

  // Map Tailwind background classes to more vibrant hex colors for better visibility
  const colorMap: Record<string, string> = {
    "bg-green-50": "#22c55e", // Green-500 for success
    "bg-red-50": "#ef4444", // Red-500 for danger
    "bg-orange-50": "#fb923c", // Orange-400 for warning (lighter)
    "bg-blue-50": "#3b82f6", // Blue-500 for info
    "bg-gray-50": "#6b7280", // Gray-500 for neutral
    "bg-gray-100": "#9ca3af", // Gray-400 for archived
    "bg-purple-50": "#a855f7", // Purple-500 for primary (no show)
  };

  return colorMap[colorConfig.bgColor] || "#3b82f6"; // default blue
}

// Get text color for event based on status
function getEventTextColor(statusId: number, paymentStatusId?: number): string {
  // Use white text for all statuses for better contrast on vibrant backgrounds
  return "#ffffff";
}

// Get border color for event based on status
function getEventBorderColor(statusId: number, paymentStatusId?: number): string {
  // Check if booking is paid - if so, return darker border
  if (paymentStatusId === STATUS_IDS.PAYMENT_PAID) {
    return "#333333"; // Dark gray border for paid bookings
  }

  // Check if booking is invoiced - if so, return purple border
  if (paymentStatusId === STATUS_IDS.PAYMENT_INVOICED) {
    return "#a855f7"; // Purple-500 border for invoiced bookings
  }
  const colorConfig = getStatusColorConfig(statusId);

  // Map border color classes to hex
  const borderColorMap: Record<string, string> = {
    "border-green-300": "#86efac",
    "border-red-300": "#fca5a5",
    "border-orange-300": "#fdba74",
    "border-blue-300": "#93c5fd",
    "border-gray-300": "#d1d5db",
    "border-gray-400": "#9ca3af",
    "border-purple-300": "#d8b4fe",
  };

  return borderColorMap[colorConfig.borderColor] || "#93c5fd";
}

function getPaymentStatusId(paymentStatus: string | null): number {
  if (!paymentStatus) return 0;

  switch (paymentStatus.toLowerCase()) {
    case "pending":
      return 20;
    case "paid":
      return 21;
    case "failed":
      return 22;
    case "refunded":
      return 23;
    case "partial":
      return 24;
    default:
      return 0;
  }
}

function getStatusBadgeVariant(
  statusId: number,
): "default" | "secondary" | "destructive" | "outline" {
  // Use centralized color config to determine badge variant
  const colorConfig = getStatusColorConfig(statusId);

  // Map color themes to badge variants
  if (colorConfig.bgColor.includes("red")) return "destructive";
  if (colorConfig.bgColor.includes("orange")) return "secondary";
  if (colorConfig.bgColor.includes("gray")) return "outline";
  if (colorConfig.bgColor.includes("green")) return "default";
  if (colorConfig.bgColor.includes("blue")) return "default";

  return "default";
}

function getPaymentStatusText(paymentStatusId: number): string {
  switch (paymentStatusId) {
    case STATUS_IDS.PAYMENT_PENDING:
      return "Pending";
    case STATUS_IDS.PAYMENT_PAID:
      return "Paid";
    case STATUS_IDS.PAYMENT_FAILED:
      return "Failed";
    case STATUS_IDS.PAYMENT_REFUNDED:
      return "Refunded";
    case STATUS_IDS.PAYMENT_PARTIAL:
      return "Partial";
    case STATUS_IDS.PAYMENT_INVOICED:
      return "Invoiced";
    default:
      return "Not Paid";
  }
}

function getPaymentBadgeStyle(paymentStatusId: number): string {
  // Use centralized color config for payment statuses
  const colorConfig = getStatusColorConfig(paymentStatusId);

  // Map color themes to badge styles
  if (colorConfig.bgColor.includes("green")) {
    return "bg-green-500/10 text-green-700 border-green-500/20";
  }
  if (colorConfig.bgColor.includes("red")) {
    return "bg-red-500/10 text-red-700 border-red-500/20";
  }
  if (colorConfig.bgColor.includes("orange")) {
    return "bg-orange-500/10 text-orange-700 border-orange-500/20";
  }
  if (colorConfig.bgColor.includes("blue")) {
    return "bg-blue-500/10 text-blue-700 border-blue-500/20";
  }
  if (colorConfig.bgColor.includes("purple")) {
    return "bg-purple-500/10 text-purple-700 border-purple-500/20";
  }

  // Default for pending/not paid
  return "bg-orange-500/10 text-orange-700 border-orange-500/20";
}
